import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import qs from 'qs';
import { debounce } from 'lodash';
import { SearchState } from '../components/search/admin-dashboard-tables/interfaces';
import fetchSearchParams from "../utils/fetchSearchParams";

const useURLSync = (syncURL) => {
  const createURL = (searchState) => {
    const {
      query,
      refinementList = {},
      hitsPerPage,
      sortBy,
      configure,
    } = searchState;

    const {
      aroundLatLng = '',
      numericFilters = [],
    } = configure;

    let lat = '';
    let lng = '';
    if (Array.isArray(aroundLatLng)) {
      [lat, lng] = aroundLatLng;
    } else {
      const coords = aroundLatLng.split(', ');
      [lat, lng] = coords;
    }

    let price = '';
    if (numericFilters.length > 0) {
      numericFilters.forEach((numFilter) => {
        if (typeof numFilter === 'string') {
          if (numFilter.includes('real_price')) {
            price = numFilter;
          }
        }
      });
    }

    let minPrice = '';
    let maxPrice = '';
    if (price) {
      if (price.includes('>=')) {
        [, minPrice] = price.split(' >= ');
      } else if (price.includes('<=')) {
        [, maxPrice] = price.split(' <= ');
      } else if (price.includes(':')) {
        [minPrice, maxPrice] = price.replace('real_price:', '').split(' TO ');
      }
    }

    const queryParameters = {
      query: query ? encodeURIComponent(query) : '',
      hitsPerPage,
      sortBy,
      lat,
      lng,
      minPrice: minPrice.trim(),
      maxPrice: maxPrice.trim(),
    };

    Object.keys(refinementList).forEach((key) => {
      if (Array.isArray(refinementList[key])) {
        queryParameters[key] = `${refinementList[key].join(',').replaceAll(' ', '+')}`;
      } else {
        queryParameters[key] = refinementList[key].replaceAll(' ', '+');
      }
    });

    Object.keys(queryParameters).forEach((key) => {
      if (!queryParameters[key]) {
        delete queryParameters[key];
      }
    });
    /**
     * Logic pertaining to any query params that might be
     * introduced/for a function outside of search
     */
    const urlParams = fetchSearchParams();

    // For Google Ad Tracking
    const googleAdKey = 'gclid';
    const googleAdValue = urlParams[googleAdKey];
    if (googleAdValue) {
      queryParameters[googleAdKey] = googleAdValue;
    }

    const searchQueryString = qs.stringify(queryParameters, { encode: false });

    return searchQueryString && `?${searchQueryString}`;
  };

  const searchStateToUrl = (searchState) => {
    if (searchState) {
      return createURL(searchState);
    }
    return '';
  };

  const urlToSearchState = ({ search }): SearchState => {
    const queryObject = qs.parse(
      search.slice(1),
      { comma: true },
    );

    const {
      hitsPerPage,
      query = '',
      lat = '',
      lng = '',
      minPrice = '',
      maxPrice = '',
      ...rest
    } = queryObject;

    const refinements = [
      'age',
      'breed_name',
      'breed',
      'category',
      'colors',
      'sex',
      'size',
      'state',
      'admin_single_breed',
    ];

    const refinementList = {};
    if (queryObject) {
      refinements.forEach((refinement) => {
        if (queryObject[refinement]) {
          refinementList[refinement] = queryObject[refinement];
        }
      });
    }

    let price = '';
    if (minPrice && maxPrice) {
      price = `real_price: ${minPrice} TO ${maxPrice}`;
    } else if (minPrice) {
      price = `real_price >= ${minPrice}`;
    } else if (maxPrice) {
      price = `real_price <= ${maxPrice}`;
    }

    const newSearchState: SearchState = {
      query: query ? decodeURIComponent(String(query)) : '',
      refinementList,
      configure: {
        aroundLatLng: [lat as string, lng as string],
        numericFilters: [
          ...price ? [price] : [],
        ],
      },
      ...rest,
    };

    const hitsPPNumber = hitsPerPage
      ? Number(hitsPerPage)
      : undefined;

    if (hitsPPNumber && Number.isInteger(hitsPPNumber)) {
      newSearchState.hitsPerPage = hitsPPNumber;
    }

    return newSearchState;
  };

  const [user] = useState(window.user);
  const [searchState, setSearchState] = useState<SearchState>(urlToSearchState(window.location));
  const onPopState = ({ state }: any) => setSearchState(state || {});

  useEffect(() => {
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const pushSearchState = useRef(
    debounce((newSearchState) => {
      window.history.pushState(
        newSearchState,
        '',
        searchStateToUrl(newSearchState),
      );
    }, 400),
  ).current;
  useEffect(() => {
    return () => {
      pushSearchState.cancel();
    };
  }, [pushSearchState]);

  useEffect(() => {
    if (syncURL) {
      pushSearchState(searchState);
    }
  }, [searchState, syncURL, pushSearchState]);

  const onSearchStateChange = (newSearchState: any) => {
    setSearchState(newSearchState);
  };

  const debouncedUpdateSearchStateQuery = useRef(
    debounce((currentSearchState, newQuery) => {
      if (currentSearchState && currentSearchState.query !== newQuery) {
        setSearchState((s) => ({
          ...s,
          query: newQuery,
        }));
      }
    }, 400),
  ).current;
  useEffect(() => {
    return () => {
      debouncedUpdateSearchStateQuery.cancel();
    };
  }, [debouncedUpdateSearchStateQuery]);

  // The code below syncs this to the query in the global search bar
  const updateQuery = useCallback((e: any) => {
    const { detail } = e as { detail: { query: () => string } };
    if (detail && detail.query) {
      const newQuery = detail.query();
      debouncedUpdateSearchStateQuery(searchState, newQuery);
    }
  }, [searchState, debouncedUpdateSearchStateQuery]);

  useEffect(() => {
    document.addEventListener('updateQuery', updateQuery);
    return () => document.removeEventListener('updateQuery', updateQuery);
  }, [updateQuery]);

  if (!syncURL) {
    return {
      user,
      searchState,
      onSearchStateChange: () => {},
      createURL: () => {},
    };
  }

  return {
    user,
    searchState,
    onSearchStateChange,
    createURL: searchStateToUrl,
  };
};

export default useURLSync;
